import { Injectable } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ComponentStore } from '@ngrx/component-store';
import { EMPTY, switchMap, tap, catchError } from 'rxjs';
import { AnswersApiService } from 'src/app/services/answers-api.service';

export interface FileState {
    readonly guid: string | null;
    readonly blob: Blob | null;
    readonly fileUrl: SafeResourceUrl | null;
    readonly fileName: string | null;
    readonly loading: boolean;
    readonly error: string | null;
}

const initialState: FileState = {
    guid: null,
    blob: null,
    fileUrl: null,
    fileName: null,
    loading: false,
    error: null
};

@Injectable()
export class FileStore extends ComponentStore<FileState> {
    private objectUrl: string | null = null;

    constructor(
        private readonly answersApiService: AnswersApiService,
        private readonly sanitizer: DomSanitizer
    ) {
        super(initialState);
    }

    readonly guid$ = this.select((state) => state.guid);
    readonly blob$ = this.select((state) => state.blob);
    readonly fileUrl$ = this.select((state) => state.fileUrl);
    readonly fileName$ = this.select((state) => state.fileName);
    readonly loading$ = this.select((state) => state.loading);
    readonly error$ = this.select((state) => state.error);
    readonly isImage$ = this.select((state) => {
        const fileName = state.fileName?.toLowerCase() ?? '';
        return (
            fileName.endsWith('.png') ||
            fileName.endsWith('.jpg') ||
            fileName.endsWith('.jpeg') ||
            fileName.endsWith('.gif') ||
            fileName.endsWith('.webp') ||
            fileName.endsWith('.bmp') ||
            fileName.endsWith('.svg')
        );
    });
    readonly vm$ = this.select({
        guid: this.guid$,
        blob: this.blob$,
        fileUrl: this.fileUrl$,
        fileName: this.fileName$,
        isImage: this.isImage$,
        loading: this.loading$,
        error: this.error$
    });

    readonly setGuid = this.updater<string | null>((state, guid) => ({
        ...state,
        guid
    }));

    readonly setLoading = this.updater<boolean>((state, loading) => ({
        ...state,
        loading
    }));

    readonly setError = this.updater<string | null>((state, error) => ({
        ...state,
        error
    }));

    readonly clearFile = this.updater((state) => {
        this.revokeObjectUrl();

        return {
            ...state,
            blob: null,
            fileUrl: null,
            error: null,
            loading: false
        };
    });

    readonly loadFileByGuid = this.effect<string>((guid$) =>
        guid$.pipe(
            tap((guid) => {
                this.revokeObjectUrl();
                this.patchState({
                    guid,
                    loading: true,
                    error: null,
                    blob: null,
                    fileUrl: null,
                    fileName: null
                });
            }),
            switchMap((guid) =>
                this.answersApiService.getFileByGuid(guid).pipe(
                    tap((response) => {
                        const blob = response.body;

                        if (!blob) {
                            this.patchState({
                                loading: false,
                                error: 'לא התקבל קובץ מהשרת',
                                blob: null,
                                fileUrl: null,
                                fileName: null
                            });
                            return;
                        }

                        const encodedFileName = response.headers.get('X-File-Name');
                        const fileName = encodedFileName
                            ? decodeURIComponent(encodedFileName)
                            : 'file';

                        this.objectUrl = URL.createObjectURL(blob);
                        const safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.objectUrl);

                        this.patchState({
                            guid,
                            blob,
                            fileUrl: safeUrl,
                            fileName,
                            loading: false,
                            error: null
                        });
                    }),
                    catchError(() => {
                        this.patchState({
                            loading: false,
                            error: 'אירעה שגיאה בטעינת הקובץ',
                            blob: null,
                            fileUrl: null,
                            fileName: null
                        });

                        return EMPTY;
                    })
                )
            )
        )
    );

    destroy(): void {
        this.revokeObjectUrl();
    }

    private revokeObjectUrl(): void {
        if (this.objectUrl) {
            URL.revokeObjectURL(this.objectUrl);
            this.objectUrl = null;
        }
    }
}